import { getPresignedHeadUrl, getPresignedUrl } from '../s3';

/** Promise wrapper around the callback-style `getPresignedUrl` S3 helper. */
export const getAudioUrl = (path: string): Promise<string> =>
  new Promise((resolve, reject) => {
    getPresignedUrl(path, (url) => {
      if (url) {
        resolve(url);
      } else {
        reject(new Error(`Could not get a presigned URL for ${path}`));
      }
    });
  });

/** Promise wrapper around the callback-style `getPresignedHeadUrl` S3 helper. */
const getAudioHeadUrl = (path: string): Promise<string> =>
  new Promise((resolve, reject) => {
    getPresignedHeadUrl(path, (url) => {
      if (url) {
        resolve(url);
      } else {
        reject(new Error(`Could not get a presigned HEAD URL for ${path}`));
      }
    });
  });

export type DownloadMode = 'parallel' | 'single';

export interface DownloadResult {
  arrayBuffer: ArrayBuffer;
  bytes: number;
  mode: DownloadMode;
}

export interface DownloadProgressInfo {
  percent: number;
  loadedBytes: number;
  /** `0` when the total size isn't known yet (e.g. before the HEAD request resolves). */
  totalBytes: number;
}

export type DownloadProgressCallback = (progress: DownloadProgressInfo) => void;

const PARALLEL_PARTS = 6;
const MIN_PART_BYTES = 2 * 1024 * 1024;
const MIN_TOTAL_BYTES_FOR_PARALLEL = 4 * 1024 * 1024;

/** Fetches `Content-Length` for `url` via a HEAD request. Returns `null` if unavailable. */
const getContentLength = async (url: string, signal?: AbortSignal): Promise<number | null> => {
  try {
    const response = await fetch(url, { method: 'HEAD', signal });
    if (!response.ok) {
      return null;
    }
    const totalHeader = response.headers.get('content-length');
    const total = totalHeader ? parseInt(totalHeader) : NaN;
    return Number.isFinite(total) && total > 0 ? total : null;
  } catch {
    return null;
  }
};

interface Part {
  start: number;
  end: number; // inclusive
}

const buildParts = (totalBytes: number): Part[] => {
  const partCount = Math.max(1, Math.min(PARALLEL_PARTS, Math.floor(totalBytes / MIN_PART_BYTES)));
  const baseSize = Math.ceil(totalBytes / partCount);

  const parts: Part[] = [];
  for (let index = 0; index < partCount; index++) {
    const start = index * baseSize;
    if (start >= totalBytes) {
      break;
    }
    const end = Math.min(start + baseSize - 1, totalBytes - 1);
    parts.push({ start, end });
  }
  return parts;
};

const fetchPart = async (
  url: string,
  part: Part,
  signal: AbortSignal | undefined,
  attempt = 0,
): Promise<Uint8Array> => {
  const expectedLength = part.end - part.start + 1;
  const response = await fetch(url, {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Range: `bytes=${part.start}-${part.end}`,
    },
    signal,
  });

  if (response.status !== 206) {
    throw new Error(`Ranged request returned status ${response.status}`);
  }

  const buffer = new Uint8Array(await response.arrayBuffer());
  if (buffer.byteLength !== expectedLength) {
    throw new Error(`Ranged request returned ${buffer.byteLength} bytes, expected ${expectedLength}`);
  }

  return buffer;
};

const fetchPartWithRetry = async (
  url: string,
  part: Part,
  signal: AbortSignal | undefined,
  onProgress: (loadedDelta: number) => void,
): Promise<Uint8Array> => {
  try {
    const buffer = await fetchPart(url, part, signal);
    onProgress(buffer.byteLength);
    return buffer;
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    const buffer = await fetchPart(url, part, signal, 1);
    onProgress(buffer.byteLength);
    return buffer;
  }
};

/** Downloads the whole body via a single streaming GET, reporting progress along the way. */
const downloadSingle = async (url: string, onProgress?: DownloadProgressCallback, signal?: AbortSignal) => {
  const response = await fetch(url, { signal });
  if (!response.ok || !response.body) {
    throw new Error(`Failed to download audio (status ${response.status})`);
  }

  const totalHeader = response.headers.get('content-length');
  const total = totalHeader ? parseInt(totalHeader) : 0;

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      if (total > 0) {
        onProgress?.({
          percent: Math.min(100, Math.round((loaded / total) * 100)),
          loadedBytes: loaded,
          totalBytes: total,
        });
      }
    }
  }

  const merged = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return { arrayBuffer: merged.buffer, bytes: loaded };
};

/**
 * Downloads the audio file at `path`. When the object size (via a HEAD
 * request) is known and large enough, splits the download into parallel
 * ranged GETs against the presigned URL; otherwise falls back to a single
 * streaming GET. Progress is aggregated across parts.
 */
export const downloadAudio = async (
  path: string,
  onProgress?: DownloadProgressCallback,
  signal?: AbortSignal,
): Promise<DownloadResult> => {
  const url = await getAudioUrl(path);

  let totalBytes: number | null = null;
  try {
    const headUrl = await getAudioHeadUrl(path);
    totalBytes = await getContentLength(headUrl, signal);
  } catch {
    totalBytes = null;
  }

  if (!totalBytes || totalBytes < MIN_TOTAL_BYTES_FOR_PARALLEL) {
    const { arrayBuffer, bytes } = await downloadSingle(url, onProgress, signal);
    return { arrayBuffer, bytes, mode: 'single' };
  }

  const parts = buildParts(totalBytes);
  if (parts.length <= 1) {
    const { arrayBuffer, bytes } = await downloadSingle(url, onProgress, signal);
    return { arrayBuffer, bytes, mode: 'single' };
  }

  try {
    let loaded = 0;
    const reportProgress = (delta: number) => {
      loaded += delta;
      onProgress?.({
        percent: Math.min(100, Math.round((loaded / totalBytes!) * 100)),
        loadedBytes: loaded,
        totalBytes: totalBytes!,
      });
    };

    const buffers = await Promise.all(parts.map((part) => fetchPartWithRetry(url, part, signal, reportProgress)));

    const merged = new Uint8Array(totalBytes);
    let offset = 0;
    for (const buffer of buffers) {
      merged.set(buffer, offset);
      offset += buffer.byteLength;
    }

    return { arrayBuffer: merged.buffer, bytes: totalBytes, mode: 'parallel' };
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    // Any part failing (e.g. 200 instead of 206, or a retry exhausted) falls
    // back to a plain single-stream download.
    const { arrayBuffer, bytes } = await downloadSingle(url, onProgress, signal);
    return { arrayBuffer, bytes, mode: 'single' };
  }
};

/**
 * Decodes a raw audio ArrayBuffer and mixes it down to a mono Float32Array
 * sampled at 16kHz (the sample rate whisper models expect).
 */
export const decodeToMono16k = async (arrayBuffer: ArrayBuffer): Promise<Float32Array> => {
  const TARGET_SAMPLE_RATE = 16000;
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  const audioContext = new AudioContextCtor({ sampleRate: TARGET_SAMPLE_RATE });

  try {
    const decoded = await audioContext.decodeAudioData(arrayBuffer);

    let audioBuffer = decoded;
    // Safari/some browsers ignore the requested sampleRate on AudioContext for
    // decodeAudioData; fall back to an OfflineAudioContext resample if needed.
    if (decoded.sampleRate !== TARGET_SAMPLE_RATE) {
      const offlineContext = new OfflineAudioContext(
        decoded.numberOfChannels,
        Math.ceil(decoded.duration * TARGET_SAMPLE_RATE),
        TARGET_SAMPLE_RATE,
      );
      const source = offlineContext.createBufferSource();
      source.buffer = decoded;
      source.connect(offlineContext.destination);
      source.start(0);
      audioBuffer = await offlineContext.startRendering();
    }

    // Mix down to mono by averaging all channels.
    const channelCount = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;
    const mono = new Float32Array(length);
    for (let channel = 0; channel < channelCount; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        mono[i] += channelData[i] / channelCount;
      }
    }

    return mono;
  } finally {
    await audioContext.close();
  }
};
